'use client'

import { useQuery as useConvexQuery } from 'convex/react'
import type { OptionalRestArgsOrSkip } from 'convex/react'
import { getFunctionName } from 'convex/server'
import type { FunctionReference, FunctionReturnType } from 'convex/server'
import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'

/**
 * Convex `useQuery`, paused while the browser tab has been hidden for a while.
 *
 * Every live subscription re-executes on each Clerk token refresh (~1/min),
 * so a tab left open in the background re-ran ~30 always-on queries all day
 * (most of the production function-call volume, 2026-10). After
 * HIDDEN_PAUSE_MS hidden, subscriptions are dropped ('skip') and the last
 * result keeps being returned, so nothing re-renders as loading. When the tab
 * is shown again they resubscribe; the held value is shown until the fresh one
 * arrives. Mutations are untouched, so saves keep running while hidden.
 *
 * Import `useQuery` from here instead of `convex/react`.
 */
export const HIDDEN_PAUSE_MS = 5 * 60 * 1000

/**
 * Kept live in a background tab: generation results are placed on the canvas
 * from getJobsByUser, and listMyRecentRuns raises the background-task
 * completion toast.
 */
const KEEP_LIVE_WHEN_HIDDEN = new Set<string>([
  'comfyJobsMutations:getJobsByUser',
  'agentRuns:listMyRecentRuns',
])

let paused = false
let pauseTimer: ReturnType<typeof setTimeout> | undefined
let listening = false
const listeners = new Set<() => void>()

function setPaused(next: boolean) {
  if (paused === next) return
  paused = next
  for (const listener of listeners) listener()
}

function onVisibilityChange() {
  clearTimeout(pauseTimer)
  if (document.visibilityState === 'hidden') {
    pauseTimer = setTimeout(() => setPaused(true), HIDDEN_PAUSE_MS)
  } else {
    setPaused(false)
  }
}

function subscribePause(listener: () => void) {
  if (!listening && typeof document !== 'undefined') {
    listening = true
    document.addEventListener('visibilitychange', onVisibilityChange)
    if (document.visibilityState === 'hidden') onVisibilityChange()
  }
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** True once the tab has been hidden for HIDDEN_PAUSE_MS; false again when shown. */
export function useHiddenTabPaused(): boolean {
  return useSyncExternalStore(
    subscribePause,
    () => paused,
    () => false,
  )
}

function keepsLiveWhenHidden(query: FunctionReference<'query'>): boolean {
  try {
    return KEEP_LIVE_WHEN_HIDDEN.has(getFunctionName(query))
  } catch {
    // Not a real function reference (test doubles): treat as pausable.
    return false
  }
}

export function useQuery<Query extends FunctionReference<'query'>>(
  query: Query,
  ...args: OptionalRestArgsOrSkip<Query>
): FunctionReturnType<Query> | undefined {
  const pause = useHiddenTabPaused() && !keepsLiveWhenHidden(query)
  const skipped = args[0] === 'skip'
  const key = skipped ? null : JSON.stringify(args[0] ?? {})

  const live = useConvexQuery(
    query,
    ...((pause ? ['skip'] : args) as OptionalRestArgsOrSkip<Query>),
  )

  // Last answer for these args, held only across a pause: never used to stand
  // in for a fresh subscription otherwise (one-shot readers rely on that).
  const last = useRef<{ key: string; value: FunctionReturnType<Query> } | null>(null)
  const holding = useRef(false)
  if (!pause && live !== undefined && key !== null) {
    last.current = { key, value: live }
    holding.current = false
  }
  if (pause) holding.current = true

  if (skipped) return undefined
  if (holding.current && last.current?.key === key) return last.current.value
  return live
}

/**
 * One-shot read: subscribe until the first answer, keep it, and drop the
 * subscription, so token refreshes and writes stop re-running a heavy query.
 * `refresh()` reads again (e.g. after a mutation the list must reflect); the
 * previous answer stays on screen until the new one arrives. New args (a
 * different user) read again.
 */
export function useQueryOnce<Query extends FunctionReference<'query'>>(
  query: Query,
  ...args: OptionalRestArgsOrSkip<Query>
): { data: FunctionReturnType<Query> | undefined; refresh: () => void } {
  const skipped = args[0] === 'skip'
  const key = skipped ? null : JSON.stringify(args[0] ?? {})
  const [epoch, setEpoch] = useState(0)
  const [captured, setCaptured] = useState<{
    key: string
    epoch: number
    value: FunctionReturnType<Query>
  } | null>(null)

  const sameArgs = key !== null && captured?.key === key
  const current = sameArgs && captured?.epoch === epoch
  const live = useConvexQuery(
    query,
    ...((skipped || current ? ['skip'] : args) as OptionalRestArgsOrSkip<Query>),
  )

  useEffect(() => {
    if (key !== null && !current && live !== undefined) setCaptured({ key, epoch, value: live })
  }, [key, epoch, current, live])

  const refresh = useCallback(() => setEpoch((e) => e + 1), [])
  if (skipped) return { data: undefined, refresh }
  return { data: sameArgs ? captured?.value : live, refresh }
}
